/**
 * Поток клипов зоны за сутки и пересборка окна потока в один файл — общее для
 * «Сегментов» и «Диалогов» (L3/L7, services/plugins/speech_to_text/TODO.md).
 *
 * glueClips — перенос services/plugins/speech_to_text/glue.py в браузер: клипы
 * раскладываются по recorded_at, края гасятся на 5 мс, наложения складываются,
 * дыры остаются тишиной. На центральном бэкенде нет ffmpeg, поэтому склейка тут, а
 * байты клипов идут через API (у бакета нет CORS). Смысл — «пересобрать файл по
 * сегментам, как у нас он считается, и увидеть, если там какие-то проблемы».
 */
import {
  getAudioRecordsStream, getClipAudioBlob, getConversations,
  type AudioRecordRaw, type Conversation, type DialogueBoundary,
} from '@/api/conversations';

export const t0 = (c: AudioRecordRaw) => Date.parse(c.recorded_at as string);
export const t1 = (c: AudioRecordRaw) => t0(c) + (c.clip_duration || 30) * 1000;

export async function loadDay(zoneId: number, day: string): Promise<AudioRecordRaw[]> {
  // Сутки по МСК: [day 00:00, day+1 00:00) по времени записи.
  const since = new Date(`${day}T00:00:00+03:00`).toISOString();
  const until = new Date(Date.parse(since) + 24 * 3600 * 1000).toISOString();
  const out: AudioRecordRaw[] = [];
  for (let page = 1; page < 50; page++) {
    const r = await getAudioRecordsStream(zoneId, since, until, page, 200);
    out.push(...r.results);
    if (page >= r.num_pages) break;
  }
  return out;
}

const SR = 16000;
const EDGE_S = 0.005;

export interface GlueReport {
  blob: Blob;
  clips: number;
  failed: number[];
  /** Дыры между соседними клипами внутри окна, секунды от начала окна. */
  holes: { at: number; dur: number }[];
  /** Клипы, наложившиеся друг на друга (прод их складывает). */
  overlaps: { at: number; dur: number }[];
}

async function decode(blob: Blob): Promise<Float32Array> {
  const buf = await blob.arrayBuffer();
  // Контекст на 16 кГц: decodeAudioData сам пересэмплирует, как ffmpeg -ar 16000 в glue.py.
  const ctx = new OfflineAudioContext(1, 1, SR);
  const ab = await ctx.decodeAudioData(buf);
  const x = new Float32Array(ab.length);
  for (let ch = 0; ch < ab.numberOfChannels; ch++) {
    const d = ab.getChannelData(ch);
    for (let i = 0; i < x.length; i++) x[i] += d[i] / ab.numberOfChannels;
  }
  return x;
}

function wav(buf: Float32Array): Blob {
  const out = new DataView(new ArrayBuffer(44 + buf.length * 2));
  const str = (o: number, s: string) => { for (let i = 0; i < s.length; i++) out.setUint8(o + i, s.charCodeAt(i)); };
  str(0, 'RIFF'); out.setUint32(4, 36 + buf.length * 2, true); str(8, 'WAVE'); str(12, 'fmt ');
  out.setUint32(16, 16, true); out.setUint16(20, 1, true); out.setUint16(22, 1, true);
  out.setUint32(24, SR, true); out.setUint32(28, SR * 2, true); out.setUint16(32, 2, true); out.setUint16(34, 16, true);
  str(36, 'data'); out.setUint32(40, buf.length * 2, true);
  for (let i = 0; i < buf.length; i++) out.setInt16(44 + i * 2, Math.round(Math.max(-1, Math.min(1, buf[i])) * 32767), true);
  return new Blob([out.buffer], { type: 'audio/wav' });
}

/** Окно [startMs, endMs) потока зоны одним wav — тем же правилом, что прод-склейка. */
export async function glueClips(all: AudioRecordRaw[], startMs: number, endMs: number): Promise<GlueReport> {
  const clips = all.filter((c) => c.recorded_at && t1(c) > startMs && t0(c) < endMs).sort((a, b) => t0(a) - t0(b));
  const failed: number[] = [];
  const pieces: { off: number; x: Float32Array }[] = [];
  let next = 0;
  const work = async () => {
    while (next < clips.length) {
      const c = clips[next++];
      try {
        const x = await decode(await getClipAudioBlob(c.id));
        const edge = Math.round(EDGE_S * SR);
        if (x.length > 2 * edge) {
          for (let i = 0; i < edge; i++) { const k = i / (edge - 1); x[i] *= k; x[x.length - 1 - i] *= k; }
        }
        pieces.push({ off: (t0(c) - startMs) / 1000, x });
      } catch { failed.push(c.id); }
    }
  };
  await Promise.all([work(), work(), work(), work()]);
  const total = Math.max(0, Math.round(((endMs - startMs) / 1000) * SR));
  const buf = new Float32Array(total);
  for (const { off, x } of pieces) {
    let i = Math.round(off * SR);
    let src = x;
    if (i < 0) { src = x.subarray(-i); i = 0; }      // клип начался до окна — берём хвост
    const j = Math.min(total, i + src.length);
    for (let k = i; k < j; k++) buf[k] += src[k - i];
  }
  const holes: GlueReport['holes'] = [];
  const overlaps: GlueReport['overlaps'] = [];
  const ok = clips.filter((c) => !failed.includes(c.id));
  for (let k = 1; k < ok.length; k++) {
    const gap = (t0(ok[k]) - t1(ok[k - 1])) / 1000;
    const at = Math.max(0, (t1(ok[k - 1]) - startMs) / 1000);
    if (gap > 0.05) holes.push({ at, dur: gap });
    else if (gap < -0.05) overlaps.push({ at: Math.max(0, (t0(ok[k]) - startMs) / 1000), dur: -gap });
  }
  return { blob: wav(buf), clips: pieces.length, failed, holes, overlaps };
}

/** Все разговоры зоны за сутки (с частичными) — для списка диалогов и фильтров разметки. */
export async function loadDayConversations(zoneId: number, day: string): Promise<Conversation[]> {
  const out: Conversation[] = [];
  for (let page = 1; page < 40; page++) {
    const r = await getConversations(zoneId, day, day, page, { withPartial: true });
    out.push(...r.results);
    if (page >= r.num_pages) break;
  }
  return out;
}

/**
 * Диалог дня для разметки (T2/T3): прод-разговор как есть, его правленая копия или новая рамка.
 *
 * Прод-разговоры — рамки по умолчанию. Правка прод-рамки в «Сегментах» создаёт копию
 * (DialogueBoundary с conversation_id), которая дальше заменяет прод-разговор; копия с
 * deleted — прод-разговор убран совсем. Рамка без conversation_id — новый диалог.
 */
export interface DayDialogue {
  key: string;
  origin: 'prod' | 'changed' | 'new';
  a: number;
  b: number;
  convId: number | null;
  item: DialogueBoundary | null;
}

/** Прод-разговоры по клипам: от начала первого до конца последнего привязанного клипа. */
export function prodFrames(clips: AudioRecordRaw[]): Map<number, { a: number; b: number }> {
  const m = new Map<number, { a: number; b: number }>();
  for (const c of clips) {
    if (c.conversation == null || !c.recorded_at) continue;
    const cur = m.get(c.conversation);
    m.set(c.conversation, cur ? { a: Math.min(cur.a, t0(c)), b: Math.max(cur.b, t1(c)) } : { a: t0(c), b: t1(c) });
  }
  return m;
}

export function mergeDialogues(clips: AudioRecordRaw[], items: DialogueBoundary[]): DayDialogue[] {
  const prod = prodFrames(clips);
  const replaced = new Set(items.filter((it) => it.conversation_id != null).map((it) => it.conversation_id as number));
  const out: DayDialogue[] = [];
  for (const [id, f] of prod) {
    if (!replaced.has(id)) out.push({ key: `conv-${id}`, origin: 'prod', a: f.a, b: f.b, convId: id, item: null });
  }
  for (const it of items) {
    if (it.deleted) continue;
    out.push({ key: `item-${it.id}`, origin: it.conversation_id != null ? 'changed' : 'new',
      a: Date.parse(it.start), b: Date.parse(it.end), convId: it.conversation_id, item: it });
  }
  return out.sort((x, y) => x.a - y.a);
}
